import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useDeliveryAuth } from '../context/DeliveryAuthContext';
import ProofOfDelivery from '../components/ProofOfDelivery';
import './ActiveDelivery.css';

const ActiveDelivery = () => {
  const { deliveryBoyData } = useDeliveryAuth();
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [locationTracking, setLocationTracking] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!deliveryBoyData) return;

    // Listen to active orders
    const ordersQuery = query(
      collection(db, 'orders'),
      where('deliveryBoy.id', '==', deliveryBoyData.id),
      where('status', 'in', ['confirmed', 'prepared', 'outForDelivery'])
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      if (!snapshot.empty) {
        const order = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        };
        setActiveOrder(order);
      } else {
        setActiveOrder(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [deliveryBoyData]);

  // GPS Location Tracking
  useEffect(() => {
    if (!activeOrder || activeOrder.status !== 'outForDelivery') {
      setLocationTracking(false);
      return;
    }

    // Check if geolocation is available
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationTracking(true);
    setLocationError(null);

    // Update location every 15 seconds
    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Update location in Firestore
          const orderRef = doc(db, 'orders', activeOrder.id);
          await updateDoc(orderRef, {
            'deliveryBoyLocation': {
              lat: latitude,
              lng: longitude,
              accuracy: position.coords.accuracy,
              updatedAt: serverTimestamp()
            }
          });

          console.log('📍 Location updated:', latitude, longitude);
        } catch (error) {
          console.error('Error updating location:', error);
          setLocationError('Failed to update location');
        }
      },
      (error) => {
        console.error('Location error:', error);
        let errorMessage = 'Unable to get location';

        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Location permission denied. Please enable location access.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'Location information unavailable';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Location request timed out';
        }

        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      setLocationTracking(false);
    };
  }, [activeOrder]);

  const updateOrderStatus = async (newStatus) => {
    if (!activeOrder || updating) return;

    try {
      setUpdating(true);

      const orderRef = doc(db, 'orders', activeOrder.id);
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };

      // Add timestamp fields for tracking
      if (newStatus === 'outForDelivery') {
        updateData['deliveryStatus.pickedUpAt'] = serverTimestamp();
      } else if (newStatus === 'delivered') {
        updateData['deliveryStatus.deliveredAt'] = serverTimestamp();
      }

      await updateDoc(orderRef, updateData);

      // If delivered, navigate back to dashboard
      if (newStatus === 'delivered') {
        setTimeout(() => {
          navigate('/delivery/dashboard');
        }, 1500);
      }

    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleGetDirections = (address) => {
    const query = encodeURIComponent(`${address.building}, ${address.street}, ${address.location}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  if (loading) {
    return (
      <div className="active-delivery">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!activeOrder) {
    return (
      <div className="active-delivery">
        <div className="no-active-container">
          <div className="no-active-icon">📦</div>
          <h2>No Active Delivery</h2>
          <p>You don't have any active deliveries right now</p>
          <button onClick={() => navigate('/delivery/dashboard')} className="back-btn">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="active-delivery">
      {/* Header */}
      <div className="active-delivery-header">
        <button onClick={() => navigate('/delivery/dashboard')} className="back-icon-btn">
          ← Back
        </button>
        <h1>Active Delivery</h1>
        <div></div>
      </div>

      {/* Order Status Timeline */}
      <div className="status-timeline">
        <div className={`timeline-step ${activeOrder.status === 'confirmed' || activeOrder.status === 'prepared' ? 'active' : 'completed'}`}>
          <div className="timeline-dot">1</div>
          <div className="timeline-label">Order Ready</div>
        </div>
        <div className="timeline-line"></div>
        <div className={`timeline-step ${activeOrder.status === 'outForDelivery' ? 'active' : activeOrder.status === 'delivered' ? 'completed' : ''}`}>
          <div className="timeline-dot">2</div>
          <div className="timeline-label">Picked Up</div>
        </div>
        <div className="timeline-line"></div>
        <div className={`timeline-step ${activeOrder.status === 'delivered' ? 'completed' : ''}`}>
          <div className="timeline-dot">3</div>
          <div className="timeline-label">Delivered</div>
        </div>
      </div>

      {/* Location Tracking Indicator */}
      {activeOrder.status === 'outForDelivery' && (
        <div className={`location-indicator ${locationTracking ? 'active' : ''}`}>
          <div className="location-icon">
            {locationTracking ? '📍' : '⚠️'}
          </div>
          <div className="location-text">
            {locationTracking ? (
              <>
                <strong>Location Tracking Active</strong>
                <p>Customer can see your live location</p>
              </>
            ) : locationError ? (
              <>
                <strong>Location Tracking Error</strong>
                <p>{locationError}</p>
              </>
            ) : (
              <>
                <strong>Enabling Location...</strong>
                <p>Please wait</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Order Details */}
      <div className="order-details-card">
        <div className="order-number">
          <h2>Order #{activeOrder.orderNumber}</h2>
          <div className={`status-badge ${activeOrder.status}`}>
            {activeOrder.status === 'confirmed' && 'Ready for Pickup'}
            {activeOrder.status === 'prepared' && 'Ready for Pickup'}
            {activeOrder.status === 'outForDelivery' && 'Out for Delivery'}
            {activeOrder.status === 'delivered' && 'Delivered'}
          </div>
        </div>

        {/* Customer Info */}
        <div className="info-section">
          <h3>👤 Customer Information</h3>
          <div className="info-row">
            <strong>Name:</strong>
            <span>{activeOrder.customerName}</span>
          </div>
          <div className="info-row">
            <strong>Phone:</strong>
            <span>{activeOrder.customerPhone}</span>
          </div>
          <div className="info-row">
            <strong>Address:</strong>
            <span>
              {activeOrder.address?.building}, {activeOrder.address?.street}, {activeOrder.address?.location}
            </span>
          </div>
          {activeOrder.notes && (
            <div className="info-row">
              <strong>Notes:</strong>
              <span>{activeOrder.notes}</span>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="info-section">
          <h3>📦 Order Items ({activeOrder.items?.length || 0})</h3>
          <div className="items-list">
            {activeOrder.items?.map((item, index) => (
              <div key={index} className="item-row">
                <span className="item-name">{item.name}</span>
                <span className="item-quantity">x{item.quantity}</span>
                <span className="item-price">₹{item.price * item.quantity}</span>
              </div>
            ))}
          </div>
          <div className="order-total">
            <strong>Total Amount:</strong>
            <strong>₹{activeOrder.totalAmount}</strong>
          </div>
        </div>

        {/* Payment Info */}
        <div className="info-section">
          <h3>💳 Payment Information</h3>
          <div className="info-row">
            <strong>Method:</strong>
            <span className={`payment-badge ${activeOrder.paymentMethod}`}>
              {activeOrder.paymentMethod === 'cod' ? '💵 Cash on Delivery' : '✅ Paid Online'}
            </span>
          </div>
          {activeOrder.paymentMethod === 'cod' && (
            <div className="cod-notice">
              ⚠️ Collect ₹{activeOrder.totalAmount} cash from customer
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button
          className="action-btn call-btn"
          onClick={() => handleCall(activeOrder.customerPhone)}
        >
          📞 Call Customer
        </button>

        <button
          className="action-btn directions-btn"
          onClick={() => handleGetDirections(activeOrder.address)}
        >
          🗺️ Get Directions
        </button>
      </div>

      {/* Status Update Buttons */}
      <div className="status-update-section">
        {(activeOrder.status === 'confirmed' || activeOrder.status === 'prepared') && (
          <button
            className="status-update-btn pickup"
            onClick={() => updateOrderStatus('outForDelivery')}
            disabled={updating}
          >
            {updating ? 'Updating...' : '✅ Mark as Picked Up'}
          </button>
        )}

        {activeOrder.status === 'outForDelivery' && (
          <button
            className="status-update-btn delivered"
            onClick={() => setShowProofModal(true)}
            disabled={updating}
          >
            {updating ? 'Updating...' : '🎉 Mark as Delivered'}
          </button>
        )}
      </div>

      {/* Proof of Delivery Modal */}
      {showProofModal && (
        <ProofOfDelivery
          order={activeOrder}
          onComplete={() => {
            setShowProofModal(false);
            setTimeout(() => {
              navigate('/delivery/dashboard');
            }, 1500);
          }}
          onCancel={() => setShowProofModal(false)}
        />
      )}
    </div>
  );
};

export default ActiveDelivery;
